'use client';

import StarIcon from '@mui/icons-material/Star';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

type Summary = {
  average: number;
  count: number;
  verdict: string;
};

export default function CompanyRatingBadge() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/api/reviews')
      .then((res) => {
        if (!res.ok) throw new Error(`Reviews API error: ${res.status}`);
        return res.json();
      })
      .then((data) => setSummary(data))
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[ReviewIO] Failed to load reviews:', err);
        }
        setError(true);
      });
  }, []);

  if (error || !summary) return null;

  const stars = Math.floor(summary.average);
  const hasHalf = summary.average - stars >= 0.5;

  return (
    <MuiLink
      href="https://www.reviews.io/company-reviews/store/lend-com-au"
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        transition: 'transform 0.2s ease, opacity 0.3s ease',
        '&:hover': { transform: 'translateY(-2px)', opacity: 0.9 },
        display: 'inline-block',
      }}
      aria-label={`Lend.com.au rating: ${summary.average.toFixed(
        1
      )} out of 5 stars based on ${summary.count} reviews on Reviews.io`}
    >
      <Stack direction="column" spacing={0.5} alignItems="flex-start" sx={{ color: 'white' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={0.5}>
            {[...Array(5)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 0.5,
                  backgroundColor: '#00B67A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StarIcon
                  sx={{
                    fontSize: 16,
                    color: 'white',
                    opacity: i < stars ? 1 : hasHalf && i === stars ? 0.65 : 0.35,
                  }}
                />
              </Box>
            ))}
          </Stack>

          <Typography
            variant="body1"
            sx={{ fontWeight: 600, letterSpacing: 0.2, fontSize: '1rem' }}
          >
            {summary.verdict} {summary.average.toFixed(1)} out of 5
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            ml: '1px',
          }}
        >
          Based on {summary.count} reviews
        </Typography>
      </Stack>
    </MuiLink>
  );
}
